/**
 * Shared chat types for all chat components
 */

/** Entity reference extracted from a tool call result */
export interface ToolCallEntity {
  /** Entity reference in table:uuid format */
  ref: string;
  /** Display name of the entity */
  title?: string | null;
  /** CRUD action type */
  action?: 'create' | 'read' | 'update' | 'delete';
  /** Optional context for nested entities (e.g. agent_id for knowledge notes) */
  context?: Record<string, string>;
}

/** Persisted tool call data (from DB) */
export interface ToolCallData {
  id: string;
  tool_name: string;
  module: string;
  status: 'running' | 'success' | 'error' | 'pending_approval';
  latency_ms?: number | null;
  input_data?: Record<string, unknown>;
  output_data?: Record<string, unknown>;
  error_message?: string | null;
  call_order: number;
  /** Extracted entity references for inline chips */
  entities?: ToolCallEntity[] | null;
  /** Link to agent pending approval (for inline approve/deny) */
  approval_id?: string | null;
  /** Approval's action_type (e.g. "briefing_write", "objectives_update") — only set when approval_id is set */
  action_type?: string | null;
  /** Approval's action_payload — includes resource metadata (block_name, document_title, etc.) */
  action_payload?: Record<string, unknown> | null;
  created_at?: string;
}

/** Message in a chat conversation */
export interface ChatMessage {
  id?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  /**
   * Server-assigned monotonic per-chat sequence number — the canonical
   * ordering & identity key for the message store. Present on every persisted
   * message (replay, REST history, in-flight snapshot, terminal turn events);
   * absent on optimistic local messages until reconciled to the server seq.
   */
  seq?: number;
  /** Persisted tool calls associated with this message */
  tool_calls?: ToolCallData[];
  /** File IDs attached to this message (images/documents for AI vision) */
  attachment_file_ids?: string[];
  /** True when this system message is a compaction summary replacing earlier turns */
  is_compaction_summary?: boolean;
  /** True when this assistant turn was cut short by the Stop button */
  interrupted?: boolean;
}

/** Tool execution event from the AI (live streaming) */
export interface ToolEvent {
  toolName: string;
  module: string;
  status: 'running' | 'success' | 'error' | 'pending_approval';
  latencyMs?: number;
  /** DB ID of the tool call (set after persistence) */
  toolCallId?: string;
  /** Error message for failed tool calls */
  errorMessage?: string;
  /** Entity references extracted from tool output */
  entities?: ToolCallEntity[];
  /** Whether this tool call requires approval */
  requiresApproval?: boolean;
  /** ID of the pending approval record */
  approvalId?: string;
}

/** Streaming state for a response being generated */
export interface StreamingState {
  /** Text being streamed from assistant */
  response?: string;
  /** Interim user speech (for voice mode) */
  transcript?: string;
  /** Tool execution status (legacy string) */
  toolStatus?: string | null;
  /** Whether the assistant is generating */
  isGenerating?: boolean;
  /** Tool events during this response (accumulated) */
  toolEvents?: ToolEvent[];
  /** Currently running tool name */
  activeToolName?: string | null;
  /** Reasoning/thinking content streamed from a reasoning-capable model
   * (DeepSeek R1, Qwen3 Max, GLM-4.6, etc.) before any visible text.
   * Used to show a "Thinking…" indicator with the actual reasoning. */
  reasoning?: string;
}

/**
 * Check if a tool call with 'success' status actually contains an error in its output.
 * MCP tools can return errors in the response body while the HTTP status is 200.
 */
export function isToolCallOutputError(toolCall: ToolCallData): boolean {
  if (toolCall.status !== 'success' || !toolCall.output_data) return false;
  try {
    const outer = toolCall.output_data.result as Record<string, unknown> | undefined;
    const inner = outer?.result as Record<string, unknown> | undefined;
    if (inner?.isError) return true;
    const content = inner?.content as Array<Record<string, unknown>> | undefined;
    if (content?.[0]?.text && typeof content[0].text === 'string') {
      const text = content[0].text;
      if (/^(Unknown tool:|Error [45]\d\d:|Request failed:)/.test(text)) {
        return true;
      }
    }
  } catch { /* ignore */ }
  return false;
}

/** Access level for an MCP module */
export type McpAccessLevel = 'read' | 'readwrite' | 'approve';

/** Data scope for a tool access row */
export type ToolDataScope = 'self' | 'user' | 'user_accessible' | 'all';

/** A single row in the unified tool access table */
export interface ToolAccessRow {
  /** Unique row ID (grant ID for persisted grants, generated for session rows) */
  id: string;
  /** MCP module name (e.g., 'sales', 'tasks', 'email') */
  module_name: string;
  /** Access level: read-only, read+write with approval, or read+write */
  access_level: McpAccessLevel;
  /** Whose data this row grants access to */
  data_scope: ToolDataScope;
  /** Target user ID when data_scope is 'user' or 'user_accessible' */
  scope_user_id?: string;
  /** Display name of the target user */
  scope_user_name?: string;
  /** Whether this row was inherited from agent configuration */
  inherited?: boolean;
  /** Whether entities created by the agent via this module are auto-shared with the granting user */
  share_created_entities?: boolean;
  /** Selected tool scopes for modules with fine-grained service selection (e.g., calendar_read, files_read, google_sheets_read) */
  tool_scopes?: string[];
}

/** MCP configuration for a chat session */
export interface McpConfig {
  /** Enabled PanLuma modules */
  enabledModules: string[];
  /** Per-module access level (read vs read+write) */
  moduleAccess?: Record<string, McpAccessLevel>;
  /** Specific enabled tools (module__tool_name format) */
  enabledTools?: string[];
  /** Specific disabled tools within enabled modules */
  disabledTools?: string[];
  /** Modules required by the embedding context — shown as selected and non-editable */
  lockedModules?: Record<string, McpAccessLevel>;
  /** Whether web search is enabled for this chat (default: true) */
  webSearchEnabled?: boolean;
  /** Whether web page fetch is enabled for this chat (default: true) */
  webFetchEnabled?: boolean;
  /** Whether web page navigation is enabled for this chat (default: false) */
  webNavigateEnabled?: boolean;
  /** Unified tool access rows (new model) */
  toolAccessRows?: ToolAccessRow[];
}

/** Props common to chat components */
export interface ChatDisplayProps {
  /** Conversation transcript */
  messages: ChatMessage[];
  /** Streaming state for real-time updates */
  streaming?: StreamingState;
  /** Whether the chat is currently active/connected */
  isActive?: boolean;
  /** Custom empty state message */
  emptyMessage?: string;
}
